import { h, Component, Host, Element, Prop, Event, EventEmitter, Watch } from "@stencil/core";
import pfx from "../../../../../../utils/style-class-prefix-helper";
import { NAVIGATION_META_LINK_TARGET_SELF } from "./constants/navigation-meta-link-constants";
import { NavigationMetaLinkTarget } from "./constants/navigation-meta-link-types";
import { StyledComponent } from "../../../../../../decorators/styled-component";
// import { InitLogging } from "../../../../../../decorators/init-logging";

/**
 * @slot [unnamed] - holds any number of HTML elements.
 */
@Component({
  tag: "eon-ui-navigation-meta-link",
  styleUrl: "navigation-meta-link.scss",
  shadow: true
})
export class NavigationMetaLink {
  // @InitLogging() sentryTransaction;
  private currentAriaLabel: string;

  @StyledComponent() component: any;
  @Element() hostElement: HTMLEonUiNavigationMetaLinkElement;

  /**
   * Raised when the link has been clicked.
   */
  @Event() navigationMetaLinkClick: EventEmitter;

  /**
   * Raised when the link has been loaded.
   */
  @Event() navigationMetaLinkLoad: EventEmitter;

  /**
   * Defines the text shown as label for the link.
   */
  @Prop() text: string;

  /**
   * Defines the target url of a Link.
   * Make sure to prefix this with a protocol if the link should display an icon automatically.
   * Valid protocols are "http://", "https://", "mailto:" and "tel:".
   * @example http://www.google.de, tel:0123456789
   */
  @Prop({ mutable: true }) href: string;

  /**
   * Specifies how the link is opened in the browser, e.g. in new window/tab.
   * @example "_blank", "_self", "_parent", "_top"
   * @default "_self"
   */
  @Prop() target: NavigationMetaLinkTarget = NAVIGATION_META_LINK_TARGET_SELF;

  /**
   * Specifies the label to use for accessibility.
   * Always provide this for pure icon buttons!
   */
  @Prop({ attribute: "a11y-aria-label", mutable: true }) a11yAriaLabel: string;

  /**
   * Set link to current if it matches the current url.
   */
  @Prop() current: boolean;

  componentWillLoad() {
    this.deprecatedFallback();
    this.computeAriaLabel();
  }

  componentDidLoad() {
    this.navigationMetaLinkLoad.emit();
  }

  deprecatedFallback() {
    this.a11yAriaLabel = this.hostElement.getAttribute("aria-label") ?? this.a11yAriaLabel;
  }

  @Watch("href")
  private computeAriaLabel() {
    if (this.a11yAriaLabel) {
      this.currentAriaLabel = this.a11yAriaLabel;
      return;
    }
  }

  handleClick(_event: Event) {
    this.navigationMetaLinkClick.emit(this.href);
  }

  render() {
    return (
      <Host>
        <a
          title={this.hostElement.getAttribute("title")}
          href={this.href}
          target={this.target}
          class={pfx({ current: !!this.current })}
          aria-label={this.currentAriaLabel}
          onClick={(event: MouseEvent) => this.handleClick(event)}
          role="menuitem"
          tabindex="0"
        >
          {this.text && (
            <span class={pfx("text-wrapper")}>
              <span class={pfx("text")}>{this.text}</span>
            </span>
          )}
        </a>
      </Host>
    );
  }
}
